<template>
  <div class="group relative overflow-hidden rounded-2xl bg-card-gradient shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark">
    <NuxtLink v-if="profile.link" :to="profile.link" class="block p-6 text-center">
      <!-- Profile Image -->
      <div class="mb-4 flex justify-center">
        <div class="relative">
          <img
            v-if="profile.image"
            :src="profile.image"
            :alt="profile.name"
            class="size-24 rounded-full object-cover shadow-lg"
          >
          <div
            v-else
            class="flex size-24 items-center justify-center rounded-full bg-gradient-to-r from-gray-200 to-gray-300 shadow-lg dark:from-gray-700 dark:to-gray-600"
          >
            <component :is="profile.placeholderIcon || PhUser" class="size-8 text-gray-500 dark:text-gray-400" />
          </div>
          <div
            v-if="profile.roleIcon"
            class="absolute -bottom-1 -right-1 flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-accent to-gradient-end"
          >
            <component :is="profile.roleIcon" class="size-4 text-white" />
          </div>
        </div>
      </div>

      <!-- Name & Title -->
      <h3 class="mb-2 text-xl font-semibold text-ternary-dark transition-colors duration-300 group-hover:text-accent dark:text-ternary-light dark:group-hover:text-accent">
        {{ profile.name }}
      </h3>
      <p class="mb-3 text-sm font-medium" :class="profile.isPlaceholder ? 'text-gray-500 dark:text-gray-400' : 'text-accent dark:text-accent'">
        {{ profile.title }}
      </p>

      <!-- Description -->
      <p class="mb-4 text-sm" :class="profile.isPlaceholder ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400'">
        {{ profile.description }}
      </p>

      <!-- Skills -->
      <div class="mb-4 flex flex-wrap justify-center gap-2">
        <span
          v-for="skill in profile.skills"
          :key="skill"
          class="rounded-full px-2 py-1 text-xs"
          :class="profile.isPlaceholder ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400' : 'bg-accent/10 text-accent'"
        >
          {{ skill }}
        </span>
      </div>

      <!-- Badge or Button -->
      <div v-if="profile.badge || profile.button" class="mt-4">
        <div
          v-if="profile.badge && !profile.button"
          class="inline-flex items-center rounded-full px-3 py-1"
          :class="profile.badgeClass || 'bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700'"
        >
          <span class="text-xs font-medium" :class="profile.badgeTextClass || 'text-gray-600 dark:text-gray-300'">
            {{ profile.badge }}
          </span>
        </div>
        <NuxtLink
          v-else-if="profile.button"
          :to="profile.buttonLink || '/contact'"
          class="inline-flex items-center rounded-full bg-gradient-to-r from-accent to-gradient-end px-4 py-2 text-sm font-medium text-white transition-all duration-300 hover:shadow-lg hover:shadow-accent/25"
        >
          {{ profile.button }}
        </NuxtLink>
      </div>

      <!-- Bottom border accent -->
      <div
        v-if="!profile.isPlaceholder"
        class="mx-auto mt-4 h-1 w-0 rounded-full bg-gradient-to-r from-accent to-gradient-end transition-all duration-500 group-hover:w-full"
      />
    </NuxtLink>

    <!-- Non-link version for placeholder cards -->
    <div v-else class="block p-6 text-center">
      <!-- Profile Image -->
      <div class="mb-4 flex justify-center">
        <div class="relative">
          <div class="flex size-24 items-center justify-center rounded-full bg-gradient-to-r from-gray-200 to-gray-300 shadow-lg dark:from-gray-700 dark:to-gray-600">
            <component :is="profile.placeholderIcon || PhUser" class="size-8 text-gray-500 dark:text-gray-400" />
          </div>
          <div
            v-if="profile.roleIcon"
            class="absolute -bottom-1 -right-1 flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-gray-400 to-gray-500"
          >
            <component :is="profile.roleIcon" class="size-4 text-white" />
          </div>
        </div>
      </div>

      <!-- Name & Title -->
      <h3 class="mb-2 text-xl font-semibold text-ternary-dark dark:text-ternary-light">
        {{ profile.name }}
      </h3>
      <p class="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ profile.title }}
      </p>

      <!-- Description -->
      <p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
        {{ profile.description }}
      </p>

      <!-- Skills -->
      <div class="mb-4 flex flex-wrap justify-center gap-2">
        <span
          v-for="skill in profile.skills"
          :key="skill"
          class="rounded-full bg-gray-200 px-2 py-1 text-xs text-gray-500 dark:bg-gray-700 dark:text-gray-400"
        >
          {{ skill }}
        </span>
      </div>

      <!-- Badge or Button -->
      <div v-if="profile.badge || profile.button" class="mt-4">
        <div
          v-if="profile.badge && !profile.button"
          class="inline-flex items-center rounded-full bg-gradient-to-r from-gray-100 to-gray-200 px-3 py-1 dark:from-gray-800 dark:to-gray-700"
        >
          <span class="text-xs font-medium text-gray-600 dark:text-gray-300">
            {{ profile.badge }}
          </span>
        </div>
        <NuxtLink
          v-else-if="profile.button"
          :to="profile.buttonLink || '/contact'"
          class="inline-flex items-center rounded-full bg-gradient-to-r from-accent to-gradient-end px-4 py-2 text-sm font-medium text-white transition-all duration-300 hover:shadow-lg hover:shadow-accent/25"
        >
          {{ profile.button }}
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PhUser } from '@phosphor-icons/vue'

interface Profile {
  name: string
  title: string
  description: string
  image?: string
  link?: string
  skills: string[]
  roleIcon?: any
  placeholderIcon?: any
  badge?: string
  badgeClass?: string
  badgeTextClass?: string
  button?: string
  buttonLink?: string
  isPlaceholder?: boolean
}

interface Props {
  profile: Profile
}

defineProps<Props>()
</script>
